import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconDeviceFloppy,
  IconEdit,
  IconExternalLink,
  IconHandStop,
  IconPlus,
  IconQuestionMark,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MOCK_PRODUCT_LINES, RETAILERS } from "../../mock/products";
import { MOCK_CATEGORIES } from "../../mock/categories";
import { LogoImg } from "../../components/LogoImg";
import type { RetailerMatch, Variant } from "../../mock/products";
import { formatPrice } from "../../utils/price";
import { formatVariantValue } from "../../utils/variant-display";
import {
  validateModelNumber,
  validateVariant,
  validateRetailerUrl,
  validateRetailerProductId,
  applyAutofixes,
  hasErrors,
  hasWarnings,
} from "../../utils/validation";
import type { ValidationResult } from "../../utils/validation";
import type { VariantAxis } from "../../types/category";

// --- Inline Validation Messages ---

function ValidationMessages({ results }: { results: ValidationResult[] }) {
  const visible = results.filter((r) => r.level !== "autofix");
  if (visible.length === 0) return null;
  return (
    <Stack gap={2} mt={4}>
      {visible.map((r, i) => (
        <Text
          key={i}
          size="xs"
          c={r.level === "error" ? "red" : "yellow.8"}
          fw={r.level === "error" ? 500 : undefined}
        >
          {r.level === "error" ? "✗ " : "⚠ "}
          {r.message}
        </Text>
      ))}
    </Stack>
  );
}


function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "< 1h ago";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// --- Price Trend Indicator ---

function PriceTrend({ rm }: { rm: RetailerMatch }) {
  if (!rm.currentPrice || !rm.priceHistory?.priceAt30d) return null;

  const current = rm.currentPrice;
  const was = rm.priceHistory.priceAt30d;
  const delta = current - was;
  const pct = ((delta / was) * 100).toFixed(1);
  const isLow = rm.priceHistory.low30d !== null && current <= rm.priceHistory.low30d;

  if (Math.abs(delta) < 0.01) return null; // flat, no indicator

  if (isLow) {
    return (
      <Tooltip label={`30-day low! Was ${formatPrice(was)} 30d ago`}>
        <Text fz={10} c="teal" fw={700} lh={1}>
          ★ LOW
        </Text>
      </Tooltip>
    );
  }

  if (delta < 0) {
    return (
      <Tooltip label={`Down ${pct}% from ${formatPrice(was)} 30d ago`}>
        <Text fz={10} c="green" fw={600} lh={1}>
          ▼ {pct}%
        </Text>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={`Up +${pct}% from ${formatPrice(was)} 30d ago`}>
      <Text fz={10} c="red" fw={600} lh={1}>
        ▲ +{pct}%
      </Text>
    </Tooltip>
  );
}

function statusIcon(status: RetailerMatch["status"]) {
  switch (status) {
    case "matched":
      return (
        <ThemeIcon size="xs" color="green" variant="light" radius="xl">
          <IconCheck size={10} />
        </ThemeIcon>
      );
    case "not_found":
      return (
        <ThemeIcon size="xs" color="gray" variant="light" radius="xl">
          <IconX size={10} />
        </ThemeIcon>
      );
    case "pending":
      return (
        <ThemeIcon size="xs" color="blue" variant="light" radius="xl">
          <IconQuestionMark size={10} />
        </ThemeIcon>
      );
    case "manual":
      return (
        <ThemeIcon size="xs" color="orange" variant="light" radius="xl">
          <IconEdit size={10} />
        </ThemeIcon>
      );
    case "confirmed_missing":
      return (
        <ThemeIcon size="xs" color="dark" variant="light" radius="xl">
          <IconHandStop size={10} />
        </ThemeIcon>
      );
  }
}

// --- Editable Model Number Cell ---

function EditableModelCell({
  variant,
  brandName,
  existingModels,
  onSave,
}: {
  variant: Variant;
  brandName: string;
  existingModels: string[];
  onSave: (id: string, model: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(variant.modelNumber ?? "");
  const [saved, setSaved] = useState(false);

  const validation = editing && value.trim().length > 0
    ? validateModelNumber(value, {
        variantLabel: variant.variantLabel,
        variantValues: variant.variantValues,
        brandName,
        existingModels: existingModels.filter((m) => m !== variant.modelNumber),
      })
    : [];

  const blocked = hasErrors(validation);

  function handleSave() {
    const fixed = applyAutofixes(value, validation);
    if (!fixed.trim() || blocked) return;
    onSave(variant.id, fixed.trim());
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (editing) {
    return (
      <Stack gap={2}>
        <Group gap={4} wrap="nowrap">
          <TextInput
            size="xs"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            placeholder="e.g. QN65S95FAFXZA"
            styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
            style={{ flex: 1 }}
            error={blocked}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setEditing(false);
            }}
          />
          <ActionIcon
            size="xs"
            color="green"
            variant="light"
            onClick={handleSave}
            disabled={blocked || !value.trim()}
          >
            <IconCheck size={12} />
          </ActionIcon>
          <ActionIcon size="xs" color="gray" variant="light" onClick={() => setEditing(false)}>
            <IconX size={12} />
          </ActionIcon>
        </Group>
        <ValidationMessages results={validation} />
        {!blocked && value.trim().length > 3 && !hasWarnings(validation) && (
          <Text size="xs" c="dimmed">
            Saving will search Amazon, Walmart, Target, and Costco for this model number.
          </Text>
        )}
      </Stack>
    );
  }

  if (variant.modelNumber) {
    return (
      <Group gap={4} wrap="nowrap">
        <Text size="sm" ff="monospace">
          {variant.modelNumber}
        </Text>
        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setEditing(true)}>
          <IconEdit size={12} />
        </ActionIcon>
      </Group>
    );
  }

  return (
    <UnstyledButton onClick={() => setEditing(true)}>
      <Group gap={4}>
        {saved ? (
          <>
            <IconCheck size={12} style={{ color: "var(--mantine-color-green-6)" }} />
            <Text size="sm" c="green">
              Saved — searching retailers...
            </Text>
          </>
        ) : (
          <>
            <IconPlus size={12} style={{ color: "var(--mantine-color-blue-5)" }} />
            <Text size="sm" c="blue" fs="italic">
              Add model number
            </Text>
          </>
        )}
      </Group>
    </UnstyledButton>
  );
}

// --- Retailer URL Modal ---

function RetailerUrlModal({
  opened,
  onClose,
  retailerMatch,
  variantLabel,
  existingUrls,
  onSave,
  onConfirmMissing,
}: {
  opened: boolean;
  onClose: () => void;
  retailerMatch: RetailerMatch;
  variantLabel: string;
  existingUrls: string[];
  onSave: (url: string, productId: string) => void;
  onConfirmMissing: () => void;
}) {
  const [url, setUrl] = useState(retailerMatch.url ?? "");
  const [productId, setProductId] = useState(retailerMatch.productId ?? "");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const urlValidation = url.trim().length > 0
    ? validateRetailerUrl(url, {
        retailerName: retailerMatch.retailerName,
        existingUrls: existingUrls.filter((u) => u !== retailerMatch.url),
      })
    : [];
  const idValidation = productId.trim().length > 0
    ? validateRetailerProductId(productId, retailerMatch.retailerName, url)
    : [];
  const urlBlocked = hasErrors(urlValidation);

  // Apply autofixes to URL
  const fixedUrl = url.trim().length > 0 ? applyAutofixes(url, urlValidation) : url;

  const isNew = !retailerMatch.url && fixedUrl.length > 0;
  const isChanged = retailerMatch.url !== null && fixedUrl !== retailerMatch.url;

  function handleVerify() {
    setVerifying(true);
    // Simulates a Bright Data PDP scrape of this URL
    setTimeout(() => {
      setVerifying(false);
      setVerified(true);
    }, 2000);
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <LogoImg
            domain={retailerMatch.retailerDomain}
            alt={retailerMatch.retailerName}
            size={20}
          />
          <Text fw={600}>
            {retailerMatch.retailerName} — {variantLabel}
          </Text>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Status banner */}
        {retailerMatch.status === "matched" && retailerMatch.currentPrice && (
          <Card withBorder padding="sm" bg="green.0">
            <Group justify="space-between">
              <Text size="sm">Tracking active</Text>
              <Text size="sm" fw={600}>
                {formatPrice(retailerMatch.currentPrice)}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Last scraped {timeAgo(retailerMatch.lastScrapedAt)}
              </Text>
              <Text size="xs" c="dimmed">
                Next scrape in ~8h
              </Text>
            </Group>
          </Card>
        )}

        {retailerMatch.status === "confirmed_missing" && (
          <Card withBorder padding="sm" bg="gray.0">
            <Text size="sm" c="dimmed">
              Confirmed not carried at this retailer. Scraper will not
              search for this product here. Click below to override.
            </Text>
          </Card>
        )}

        {/* URL input */}
        <div>
          <TextInput
            label="Product URL"
            placeholder={`https://www.${retailerMatch.retailerDomain}/...`}
            value={url}
            onChange={(e) => {
              setUrl(e.currentTarget.value);
              setVerified(false);
            }}
            error={urlBlocked}
            rightSection={
              fixedUrl ? (
                <Anchor href={fixedUrl} target="_blank" size="xs">
                  <IconExternalLink size={14} />
                </Anchor>
              ) : null
            }
          />
          <ValidationMessages results={urlValidation} />
        </div>

        {/* Retailer-specific ID */}
        <div>
          <TextInput
            label={
              retailerMatch.retailerName === "Amazon"
                ? "ASIN"
                : retailerMatch.retailerName === "Best Buy"
                  ? "Best Buy SKU"
                  : retailerMatch.retailerName === "Walmart"
                    ? "Walmart Item ID"
                    : "Retailer Product ID"
            }
            placeholder={
              retailerMatch.retailerName === "Amazon"
                ? "e.g. B0DV2GXKM1"
                : retailerMatch.retailerName === "Best Buy"
                  ? "e.g. 6613459"
                  : "Optional"
            }
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
            styles={{ input: { fontFamily: "monospace" } }}
          />
          <ValidationMessages results={idValidation} />
        </div>

        {/* Context message */}
        <Text size="xs" c="dimmed">
          {retailerMatch.status === "not_found"
            ? "Automatic search didn't find this product. Paste the URL if you've located it manually."
            : retailerMatch.status === "pending"
              ? "This retailer hasn't been searched yet. Paste a URL or wait for auto-discovery."
              : retailerMatch.status === "confirmed_missing"
                ? "Previously confirmed as not carried. Add a URL to re-activate tracking."
                : retailerMatch.status === "manual"
                  ? "Manually entered — use Verify to confirm it scrapes correctly."
                  : ""}
        </Text>

        {/* Verify button — shows when URL is new or changed */}
        {(isNew || isChanged || retailerMatch.status === "manual") &&
          url.length > 10 && (
            <Card withBorder padding="sm">
              {verified ? (
                <Group gap="sm">
                  <ThemeIcon size="sm" color="green" variant="light" radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text size="sm" fw={500} c="green">
                      Verified — scraper returned data
                    </Text>
                    <Text size="xs" c="dimmed">
                      URL is valid. Saving will add to the daily scrape schedule.
                    </Text>
                  </Stack>
                </Group>
              ) : (
                <Group justify="space-between">
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {isNew ? "New URL" : "URL changed"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Run a test scrape to confirm this URL returns the right
                      product and price before adding to the schedule.
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="light"
                    loading={verifying}
                    onClick={handleVerify}
                  >
                    Verify now
                  </Button>
                </Group>
              )}
            </Card>
          )}

        {/* What happens on save */}
        {url.length > 10 && (
          <Card withBorder padding="xs" bg="blue.0">
            <Text size="xs" fw={500} c="blue">
              On save, this will:
            </Text>
            <Stack gap={2} mt={4}>
              <Text size="xs" c="dimmed">
                1. Create a tracking record for {variantLabel} at{" "}
                {retailerMatch.retailerName}
              </Text>
              <Text size="xs" c="dimmed">
                2. Queue an immediate price scrape via Bright Data
              </Text>
              <Text size="xs" c="dimmed">
                3. Add to the daily scrape schedule for ongoing monitoring
              </Text>
            </Stack>
          </Card>
        )}

        <Divider />

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconHandStop size={14} />}
            onClick={() => {
              onConfirmMissing();
              onClose();
            }}
          >
            Not carried here
          </Button>
          <Group gap="sm">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSave(fixedUrl, productId.trim());
                onClose();
              }}
              disabled={!fixedUrl || urlBlocked}
            >
              {retailerMatch.status === "matched"
                ? "Update"
                : "Save & Start Tracking"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// --- Clickable Retailer Matrix Cell ---

function RetailerCell({
  rm,
  variantLabel,
  existingUrls,
}: {
  rm: RetailerMatch;
  variantLabel: string;
  existingUrls: string[];
}) {
  const [opened, { open, close }] = useDisclosure(false);

  const bgColor =
    rm.status === "matched"
      ? undefined
      : rm.status === "not_found"
        ? "var(--mantine-color-gray-0)"
        : rm.status === "confirmed_missing"
          ? "var(--mantine-color-dark-0)"
          : rm.status === "manual"
            ? "var(--mantine-color-orange-0)"
            : undefined;

  return (
    <>
      <RetailerUrlModal
        opened={opened}
        onClose={close}
        retailerMatch={rm}
        variantLabel={variantLabel}
        existingUrls={existingUrls}
        onSave={() => {}}
        onConfirmMissing={() => {}}
      />
      <Table.Td
        ta="center"
        style={{
          cursor: "pointer",
          backgroundColor: bgColor,
          transition: "background-color 150ms",
        }}
        onClick={open}
      >
        <Tooltip
          multiline
          w={280}
          label={
            <Stack gap={2}>
              <Text size="xs" fw={600}>
                {rm.retailerName} — {rm.status === "confirmed_missing" ? "not carried" : rm.status}
              </Text>
              {rm.productId && <Text size="xs">ID: {rm.productId}</Text>}
              {rm.currentPrice && (
                <Text size="xs">
                  Price: {formatPrice(rm.currentPrice)}
                </Text>
              )}
              {rm.lastScrapedAt && (
                <Text size="xs">
                  Last scraped: {timeAgo(rm.lastScrapedAt)}
                </Text>
              )}
              {rm.priceHistory && (
                <>
                  <Text size="xs" fw={500} mt={4}>Price lows</Text>
                  <Group gap="md">
                    {rm.priceHistory.low30d && <Text size="xs">30d: {formatPrice(rm.priceHistory.low30d)}</Text>}
                    {rm.priceHistory.low60d && <Text size="xs">60d: {formatPrice(rm.priceHistory.low60d)}</Text>}
                    {rm.priceHistory.low90d && <Text size="xs">90d: {formatPrice(rm.priceHistory.low90d)}</Text>}
                  </Group>
                </>
              )}
              <Text size="xs" c="dimmed" fs="italic">
                Click to edit
              </Text>
            </Stack>
          }
        >
          <Box>
            <Stack gap={1} align="center">
              <Group gap={4} justify="center" wrap="nowrap">
                {statusIcon(rm.status)}
                {rm.currentPrice ? (
                  <Text size="xs">{formatPrice(rm.currentPrice)}</Text>
                ) : rm.status === "confirmed_missing" ? (
                  <Text size="xs" c="dimmed">
                    N/A
                  </Text>
                ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              )}
              </Group>
              <PriceTrend rm={rm} />
            </Stack>
          </Box>
        </Tooltip>
      </Table.Td>
    </>
  );
}

// --- Add Variant Modal ---

function AddVariantModal({
  opened,
  onClose,
  variantAxes,
  existingLabels,
  existingMsrps,
}: {
  opened: boolean;
  onClose: () => void;
  variantAxes: VariantAxis[];
  existingLabels: string[];
  existingMsrps: { label: string; sortValue: number; msrp: number }[];
}) {
  const [axisValues, setAxisValues] = useState<Record<string, number | string>>({});
  const [model, setModel] = useState("");
  const [msrp, setMsrp] = useState<number | string>("");

  const msrpNum = typeof msrp === "number" ? msrp : null;

  // Build variantLabel from current axis values
  const variantLabel = variantAxes
    .map((axis) => {
      const val = axisValues[axis.key];
      if (val === undefined || val === "") return null;
      return formatVariantValue(val, axis);
    })
    .filter(Boolean)
    .join(" / ");

  const hasAllAxes = variantAxes.every((axis) => {
    const val = axisValues[axis.key];
    return val !== undefined && val !== "";
  });

  const validation = hasAllAxes
    ? validateVariant(axisValues, variantLabel, msrpNum, {
        existingLabels,
        existingMsrps,
      })
    : [];
  const blocked = hasErrors(validation);
  const variantErrors = validation.filter((v) => v.field === "variant" || v.field === "size");
  const msrpResults = validation.filter((v) => v.field === "msrp");

  function handleAxisChange(key: string, value: number | string) {
    setAxisValues((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add Variant">
      <Stack gap="md">
        {variantAxes.map((axis) => (
          <div key={axis.key}>
            {axis.type === "integer" || axis.type === "decimal" ? (
              <NumberInput
                label={`${axis.label}${axis.unit ? ` (${axis.unit})` : ""}`}
                placeholder={`e.g. ${axis.type === "integer" ? "65" : "6.7"}`}
                value={axisValues[axis.key] ?? ""}
                onChange={(val) => handleAxisChange(axis.key, val)}
                suffix={axis.unit}
                allowDecimal={axis.type === "decimal"}
                error={variantErrors.some((v) => v.level === "error")}
              />
            ) : (
              <TextInput
                label={axis.label}
                placeholder={`Enter ${axis.label.toLowerCase()}`}
                value={String(axisValues[axis.key] ?? "")}
                onChange={(e) => handleAxisChange(axis.key, e.currentTarget.value)}
                error={variantErrors.some((v) => v.level === "error")}
              />
            )}
            <ValidationMessages results={variantErrors} />
          </div>
        ))}
        <TextInput
          label="Model Number"
          placeholder="e.g. QN42S90FAFXZA (optional)"
          value={model}
          onChange={(e) => setModel(e.currentTarget.value)}
          styles={{ input: { fontFamily: "monospace" } }}
        />
        <div>
          <NumberInput
            label="MSRP"
            placeholder="e.g. 1099.99"
            value={msrp}
            onChange={setMsrp}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            error={msrpResults.some((v) => v.level === "error")}
          />
          <ValidationMessages results={msrpResults} />
        </div>
        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!hasAllAxes || blocked} onClick={onClose}>
            Add {variantLabel || "Variant"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// === MAIN PAGE ===

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pl = MOCK_PRODUCT_LINES.find((p) => p.id === id);
  const [addVariantOpened, { open: openAddVariant, close: closeAddVariant }] =
    useDisclosure(false);

  const category = pl ? MOCK_CATEGORIES.find((c) => c.id === pl.categoryId) : undefined;
  const variantAxes = category?.attributeSchema.variantAxes ?? [];

  if (!pl) {
    return (
      <Stack>
        <Title order={2}>Product line not found</Title>
        <Button variant="light" onClick={() => navigate("/admin/products")}>
          Back
        </Button>
      </Stack>
    );
  }

  const totalVariantRetailers = pl.retailerMap.reduce(
    (s, vrm) =>
      s + vrm.retailers.filter((r) => r.status !== "pending").length,
    0,
  );
  const matchedVariantRetailers = pl.retailerMap.reduce(
    (s, vrm) =>
      s + vrm.retailers.filter((r) => r.status === "matched").length,
    0,
  );
  const modelsFound = pl.variants.filter((v) => v.modelNumber).length;
  const allTrackedUrls = pl.retailerMap
    .flatMap((vrm) => vrm.retailers)
    .map((rm) => rm.url)
    .filter((u): u is string => u !== null && u.length > 0);

  return (
    <>
      <AddVariantModal
        opened={addVariantOpened}
        onClose={closeAddVariant}
        variantAxes={variantAxes}
        existingLabels={pl.variants.map((v) => v.variantLabel)}
        existingMsrps={pl.variants.filter((v) => v.msrp).map((v) => ({
          label: v.variantLabel,
          sortValue: typeof v.variantValues.screen_size_inches === "number" ? v.variantValues.screen_size_inches : 0,
          msrp: v.msrp!,
        }))}
      />

      <Breadcrumbs mb="md" fz="sm">
        <Anchor onClick={() => navigate("/admin/products")} fz="sm">
          Product Lines
        </Anchor>
        <Text fz="sm" c="dimmed">
          {pl.name}
        </Text>
      </Breadcrumbs>

      <Group justify="space-between" mb="lg">
        <Box>
          <Group gap="sm">
            <LogoImg domain={pl.brandDomain} alt={pl.brandName} size={28} />
            <Title order={2}>{pl.name}</Title>
            <Badge variant="light" size="lg">
              {pl.year}
            </Badge>
            <Badge
              variant="light"
              color={
                pl.enrichmentStatus === "complete"
                  ? "green"
                  : pl.enrichmentStatus === "partial"
                    ? "yellow"
                    : "gray"
              }
            >
              {pl.enrichmentStatus}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed" mt={4}>
            {pl.displayTechnology} · {pl.categoryName} ·{" "}
            {pl.variants.length} variants · {modelsFound}/{pl.variants.length}{" "}
            model numbers · {matchedVariantRetailers}/
            {totalVariantRetailers} retailer matches
          </Text>
        </Box>
        <Group gap="sm">
          <Button variant="light" leftSection={<IconRefresh size={16} />}>
            Re-enrich
          </Button>
          <Button leftSection={<IconDeviceFloppy size={16} />}>
            Save Changes
          </Button>
        </Group>
      </Group>

      <Divider mb="lg" />

      {/* === SIZE VARIANTS === */}
      <Group justify="space-between" mb="md">
        <Title order={4}>Variants</Title>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={openAddVariant}
        >
          Add Variant
        </Button>
      </Group>

      <Table striped verticalSpacing="xs" fz="sm" mb="xl">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={70}>Variant</Table.Th>
            <Table.Th>Model Number</Table.Th>
            <Table.Th ta="right">MSRP</Table.Th>
            <Table.Th ta="right">Current Best</Table.Th>
            <Table.Th ta="right">Low 30d</Table.Th>
            <Table.Th ta="right">Low 60d</Table.Th>
            <Table.Th ta="right">Low 90d</Table.Th>
            <Table.Th ta="center">Source</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pl.variants.map((v) => {
            // Compute cross-retailer price summary for this size
            const vrm = pl.retailerMap.find((m) => m.variantId === v.id);
            const matchedRetailers = vrm?.retailers.filter((r) => r.status === "matched" && r.currentPrice) ?? [];
            const prices = matchedRetailers.map((r) => r.currentPrice!);
            const bestPrice = prices.length > 0 ? Math.min(...prices) : null;
            const bestRetailer = bestPrice !== null ? matchedRetailers.find((r) => r.currentPrice === bestPrice) : null;

            const allHistories = matchedRetailers.map((r) => r.priceHistory).filter(Boolean);
            const low30d = allHistories.length > 0 ? Math.min(...allHistories.map((h) => h!.low30d ?? Infinity)) : null;
            const low60d = allHistories.length > 0 ? Math.min(...allHistories.map((h) => h!.low60d ?? Infinity)) : null;
            const low90d = allHistories.length > 0 ? Math.min(...allHistories.map((h) => h!.low90d ?? Infinity)) : null;
            const isCurrentLow = bestPrice !== null && low30d !== null && bestPrice <= low30d;

            return (
            <Table.Tr key={v.id}>
              <Table.Td>
                <Badge variant="light" size="sm">
                  {v.variantLabel}
                </Badge>
              </Table.Td>
              <Table.Td>
                <EditableModelCell
                  variant={v}
                  brandName={pl.brandName}
                  existingModels={pl.variants.filter((x) => x.modelNumber).map((x) => x.modelNumber!)}
                  onSave={() => {}}
                />
              </Table.Td>
              <Table.Td ta="right">
                {v.msrp ? (
                  <Text size="sm" c="dimmed">
                    {formatPrice(v.msrp)}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">—</Text>
                )}
              </Table.Td>
              <Table.Td ta="right">
                {bestPrice !== null ? (
                  <Tooltip label={`Best price at ${bestRetailer?.retailerName ?? "?"}`}>
                    <Text size="sm" fw={600} c={isCurrentLow ? "teal" : undefined}>
                      {isCurrentLow ? "★ " : ""}{formatPrice(bestPrice)}
                    </Text>
                  </Tooltip>
                ) : v.currentPrice ? (
                  <Text size="sm" fw={500}>
                    {formatPrice(v.currentPrice)}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">—</Text>
                )}
              </Table.Td>
              <Table.Td ta="right">
                {low30d !== null && low30d !== Infinity ? (
                  <Text size="xs" c="dimmed">{formatPrice(low30d)}</Text>
                ) : <Text size="xs" c="dimmed">—</Text>}
              </Table.Td>
              <Table.Td ta="right">
                {low60d !== null && low60d !== Infinity ? (
                  <Text size="xs" c="dimmed">{formatPrice(low60d)}</Text>
                ) : <Text size="xs" c="dimmed">—</Text>}
              </Table.Td>
              <Table.Td ta="right">
                {low90d !== null && low90d !== Infinity ? (
                  <Text size="xs" c="dimmed">{formatPrice(low90d)}</Text>
                ) : <Text size="xs" c="dimmed">—</Text>}
              </Table.Td>
              <Table.Td ta="center">
                {v.modelNumber ? (
                  <Tooltip label="Model number from Amazon PDP">
                    <Badge size="xs" variant="light" color="orange">
                      Amazon
                    </Badge>
                  </Tooltip>
                ) : (
                  <Badge size="xs" variant="light" color="gray">
                    pending
                  </Badge>
                )}
              </Table.Td>
            </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Divider mb="lg" />

      {/* === RETAILER COVERAGE MATRIX === */}
      <Group justify="space-between" mb="md">
        <Title order={4}>Retailer Coverage</Title>
        <Group gap="xs">
          {(
            [
              ["green", "Matched", IconCheck],
              ["orange", "Manual", IconEdit],
              ["gray", "Not found", IconX],
              ["dark", "Not carried", IconHandStop],
              ["blue", "Pending", IconQuestionMark],
            ] as const
          ).map(([color, label, Icon]) => (
            <Badge
              key={label}
              size="xs"
              variant="light"
              color={color}
              leftSection={<Icon size={8} />}
            >
              {label}
            </Badge>
          ))}
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb="sm">
        Click any cell to add or edit a retailer URL. Use &quot;Not carried
        here&quot; to confirm a retailer doesn&apos;t stock that size.
      </Text>

      <Card withBorder padding="md" radius="md" mb="xl">
        <Table.ScrollContainer minWidth={800}>
          <Table fz="xs" verticalSpacing={8}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={80}>Variant</Table.Th>
                {RETAILERS.map((r) => (
                  <Table.Th key={r.id} ta="center">
                    <Group gap={4} justify="center">
                      <LogoImg domain={r.domain} alt={r.name} size={14} />
                      <Text size="xs" fw={500}>
                        {r.name}
                      </Text>
                    </Group>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pl.retailerMap.map((vrm) => (
                <Table.Tr key={vrm.variantId}>
                  <Table.Td>
                    <Badge variant="light" color="gray" size="xs">
                      {vrm.variantLabel}
                    </Badge>
                  </Table.Td>
                  {vrm.retailers.map((rm) => (
                    <RetailerCell
                      key={rm.retailerId}
                      rm={rm}
                      variantLabel={vrm.variantLabel}
                      existingUrls={allTrackedUrls}
                    />
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Divider mb="lg" />

      {/* === PRODUCT IDENTITY === */}
      <Title order={4} mb="md">
        Product Identity
      </Title>

      <Grid mb="xl">
        <Grid.Col span={3}>
          <Text size="xs" c="dimmed">Brand</Text>
          <Text size="sm" fw={500}>{pl.brandName}</Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="xs" c="dimmed">Series</Text>
          <Text size="sm" fw={500}>{pl.series}</Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text size="xs" c="dimmed">Year</Text>
          <Text size="sm" fw={500}>{pl.year}</Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs" c="dimmed">Display Technology</Text>
          <Text size="sm" fw={500}>{pl.displayTechnology}</Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="xs" c="dimmed">Category</Text>
          <Text size="sm" fw={500}>{pl.categoryName}</Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <Text size="xs" c="dimmed">Discovered From</Text>
          <Badge variant="light" size="sm">
            {pl.discoveredFrom === "bestbuy" ? "Best Buy" : pl.discoveredFrom}
          </Badge>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text size="xs" c="dimmed">Description</Text>
          <Text size="sm">{pl.description}</Text>
        </Grid.Col>
      </Grid>
    </>
  );
}
